import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle, MessageCircle, ShieldCheck, Loader2, Phone, Building2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";

interface CloudPhoneOption {
  phone_number_id: string;
  display_phone_number: string | null;
  verified_name: string | null;
  code_verification_status: string | null;
  is_on_biz_app: boolean;
}

interface CloudWabaOption {
  waba_id: string;
  business_id: string | null;
  name: string | null;
}

export default function WhatsAppCloudOAuthResult() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { workspaceId } = useWorkspace(user?.id);
  const success = params.get("success") === "1";
  const phone = params.get("phone");
  const error = params.get("error");
  const needsVerification = params.get("needs_verification") === "1";
  const needsPhoneSelection = params.get("needs_phone_selection") === "1";
  const needsWabaSelection = params.get("needs_waba_selection") === "1";
  const wabaId = params.get("waba");
  const phoneNumberId = params.get("phone_number_id");

  const [step, setStep] = useState<"idle" | "code_sent">("idle");
  const [codeMethod, setCodeMethod] = useState<"SMS" | "VOICE">("SMS");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [phoneOptions, setPhoneOptions] = useState<CloudPhoneOption[]>([]);
  const [wabaOptions, setWabaOptions] = useState<CloudWabaOption[]>([]);
  const [integrationId, setIntegrationId] = useState<string | null>(null);
  const [loadingPhones, setLoadingPhones] = useState(false);
  const [loadingWabas, setLoadingWabas] = useState(false);

  useEffect(() => {
    if (success) {
      toast({ title: "WhatsApp Cloud conectado", description: phone ? `Número ${phone} pronto para uso.` : "Integração concluída." });
    } else if (error && !needsVerification && !needsPhoneSelection && !needsWabaSelection) {
      toast({ title: "Falha ao conectar", description: error, variant: "destructive" });
    }
  }, [success, phone, error, needsVerification, needsPhoneSelection, needsWabaSelection, toast]);

  useEffect(() => {
    if (!needsWabaSelection || !workspaceId) return;
    let cancelled = false;
    (async () => {
      setLoadingWabas(true);
      try {
        const instanceKey = `oauth:${workspaceId}`;
        const { data, error: fetchErr } = await supabase
          .from("integrations")
          .select("id, config")
          .eq("provider", "whatsapp_cloud")
          .eq("instance_identifier", instanceKey)
          .maybeSingle();
        if (fetchErr || !data) {
          toast({ title: "Integração não encontrada", description: "Reconecte em Integrações.", variant: "destructive" });
          return;
        }
        if (cancelled) return;
        const cfg = (data.config || {}) as Record<string, unknown>;
        const wabas = (cfg.wabas || []) as CloudWabaOption[];
        setWabaOptions(wabas);
        setIntegrationId(data.id);
      } finally {
        if (!cancelled) setLoadingWabas(false);
      }
    })();
    return () => { cancelled = true; };
  }, [needsWabaSelection, workspaceId, toast]);

  useEffect(() => {
    if (!needsPhoneSelection || !wabaId) return;
    let cancelled = false;
    (async () => {
      setLoadingPhones(true);
      try {
        const instanceKey = `waba:${wabaId}`;
        const { data, error: fetchErr } = await supabase
          .from("integrations")
          .select("id, config")
          .eq("provider", "whatsapp_cloud")
          .eq("instance_identifier", instanceKey)
          .maybeSingle();
        if (fetchErr || !data) {
          toast({ title: "Integração não encontrada", description: "Reconecte em Integrações.", variant: "destructive" });
          return;
        }
        if (cancelled) return;
        const cfg = (data.config || {}) as Record<string, unknown>;
        const phones = (cfg.phone_numbers || []) as CloudPhoneOption[];
        setPhoneOptions(phones);
        setIntegrationId(data.id);
      } finally {
        if (!cancelled) setLoadingPhones(false);
      }
    })();
    return () => { cancelled = true; };
  }, [needsPhoneSelection, wabaId, toast]);

  async function selectWaba(selectedId: string) {
    if (!integrationId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("whatsapp-cloud-verify", {
        body: { action: "select_waba", integration_id: integrationId, waba_id: selectedId },
      });
      if (error || !data?.ok) {
        const msg = (data as any)?.error?.message ?? (data as any)?.error ?? error?.message ?? "Falha ao selecionar conta";
        toast({ title: "Erro", description: typeof msg === "string" ? msg : JSON.stringify(msg), variant: "destructive" });
        return;
      }
      if (data.needs_phone_selection) {
        navigate(`/oauth/whatsapp-cloud?needs_phone_selection=1&waba=${selectedId}&phone_count=${data.phone_count ?? ""}`);
        return;
      }
      if (data.needs_verification) {
        navigate(`/oauth/whatsapp-cloud?needs_verification=1&phone_number_id=${data.phone_number_id}&phone=${encodeURIComponent(data.display_phone_number || data.phone_number_id)}&waba=${selectedId}`);
        return;
      }
      toast({ title: "Conta conectada", description: data.display_phone_number || selectedId });
      navigate("/integrations");
    } finally {
      setLoading(false);
    }
  }

  async function selectPhone(selectedId: string) {
    if (!integrationId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("whatsapp-cloud-verify", {
        body: { action: "select_phone", integration_id: integrationId, phone_number_id: selectedId },
      });
      if (error || !data?.ok) {
        const msg = (data as any)?.error?.message ?? (data as any)?.error ?? error?.message ?? "Falha ao selecionar número";
        toast({ title: "Erro", description: typeof msg === "string" ? msg : JSON.stringify(msg), variant: "destructive" });
        return;
      }
      if (data.needs_verification) {
        navigate(`/oauth/whatsapp-cloud?needs_verification=1&phone_number_id=${selectedId}&phone=${encodeURIComponent(data.display_phone_number || selectedId)}&waba=${wabaId || ""}`);
        return;
      }
      toast({ title: "Número selecionado", description: data.display_phone_number || selectedId });
      navigate("/integrations");
    } finally {
      setLoading(false);
    }
  }

  async function requestCode() {
    if (!phoneNumberId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("whatsapp-cloud-verify", {
        body: { action: "request_code", phone_number_id: phoneNumberId, code_method: codeMethod, language: "pt_BR" },
      });
      if (error || !data?.ok) {
        const msg = (data as any)?.error?.message ?? (data as any)?.error ?? error?.message ?? "Falha ao solicitar código";
        toast({ title: "Erro", description: typeof msg === "string" ? msg : JSON.stringify(msg), variant: "destructive" });
        return;
      }
      setStep("code_sent");
      toast({ title: "Código enviado", description: `Verifique o ${codeMethod === "SMS" ? "SMS" : "atendimento de voz"} no número ${phone ?? phoneNumberId}.` });
    } finally {
      setLoading(false);
    }
  }

  async function verifyCode() {
    if (!phoneNumberId || !code) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("whatsapp-cloud-verify", {
        body: { action: "verify_code", phone_number_id: phoneNumberId, code },
      });
      if (error || !data?.ok) {
        const msg = (data as any)?.error?.message ?? (data as any)?.error ?? error?.message ?? "Código inválido";
        toast({ title: "Erro", description: typeof msg === "string" ? msg : JSON.stringify(msg), variant: "destructive" });
        return;
      }
      toast({ title: "Número verificado", description: "WhatsApp Cloud pronto para uso." });
      navigate("/integrations");
    } finally {
      setLoading(false);
    }
  }

  if (needsWabaSelection) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="rounded-full p-2 bg-primary/10">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" /> Escolher conta WhatsApp
                </CardTitle>
                <CardDescription>
                  Sua Meta Business possui mais de uma conta WABA. Selecione qual conectar.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {loadingWabas && (
              <div className="flex items-center justify-center py-6 text-muted-foreground">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Carregando contas…
              </div>
            )}
            {!loadingWabas && wabaOptions.map((opt) => (
              <Button
                key={opt.waba_id}
                variant="outline"
                className="w-full justify-start h-auto py-3"
                disabled={loading}
                onClick={() => selectWaba(opt.waba_id)}
              >
                <div className="text-left">
                  <div className="font-medium">{opt.name || `WABA ${opt.waba_id}`}</div>
                  <div className="text-xs text-muted-foreground">ID: {opt.waba_id}</div>
                </div>
              </Button>
            ))}
            <Button variant="ghost" onClick={() => navigate("/integrations")} className="w-full">
              Cancelar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (needsPhoneSelection && wabaId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="rounded-full p-2 bg-primary/10">
                <Phone className="w-6 h-6 text-primary" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" /> Escolher número
                </CardTitle>
                <CardDescription>
                  Sua conta possui mais de um número WhatsApp. Selecione qual conectar.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {loadingPhones && (
              <div className="flex items-center justify-center py-6 text-muted-foreground">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Carregando números…
              </div>
            )}
            {!loadingPhones && phoneOptions.map((opt) => (
              <Button
                key={opt.phone_number_id}
                variant="outline"
                className="w-full justify-start h-auto py-3"
                disabled={loading}
                onClick={() => selectPhone(opt.phone_number_id)}
              >
                <div className="text-left">
                  <div className="font-medium">{opt.display_phone_number || opt.phone_number_id}</div>
                  {opt.verified_name && (
                    <div className="text-xs text-muted-foreground">{opt.verified_name}</div>
                  )}
                  {opt.is_on_biz_app && (
                    <div className="text-xs text-muted-foreground">Coexistência (WhatsApp Business App)</div>
                  )}
                </div>
              </Button>
            ))}
            <Button variant="ghost" onClick={() => navigate("/integrations")} className="w-full">
              Cancelar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (needsVerification && phoneNumberId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="rounded-full p-2 bg-primary/10">
                <ShieldCheck className="w-6 h-6 text-primary" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" /> Verificar número
                </CardTitle>
                <CardDescription>
                  {phone ? `Confirme a propriedade do número ${phone}` : "Confirme a propriedade do número"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === "idle" && (
              <>
                <p className="text-sm text-muted-foreground">
                  A Meta exige um código de verificação para ativar este número na Cloud API. Escolha como receber:
                </p>
                <div className="flex gap-2">
                  <Button variant={codeMethod === "SMS" ? "default" : "outline"} className="flex-1" onClick={() => setCodeMethod("SMS")}>SMS</Button>
                  <Button variant={codeMethod === "VOICE" ? "default" : "outline"} className="flex-1" onClick={() => setCodeMethod("VOICE")}>Chamada</Button>
                </div>
                <Button onClick={requestCode} disabled={loading} className="w-full">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Enviar código
                </Button>
              </>
            )}
            {step === "code_sent" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="code">Código recebido (6 dígitos)</Label>
                  <Input id="code" inputMode="numeric" maxLength={8} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" />
                </div>
                <Button onClick={verifyCode} disabled={loading || code.length < 6} className="w-full">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Verificar e ativar
                </Button>
                <Button variant="ghost" onClick={() => setStep("idle")} className="w-full">Reenviar código</Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className={`rounded-full p-2 ${success ? "bg-green-500/10" : "bg-destructive/10"}`}>
              {success ? <CheckCircle2 className="w-6 h-6 text-green-500" /> : <XCircle className="w-6 h-6 text-destructive" />}
            </div>
            <div>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" /> WhatsApp Cloud
              </CardTitle>
              <CardDescription>{success ? "Conexão estabelecida com sucesso" : "Não foi possível conectar"}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {success && phone && <p className="text-sm">Número conectado: <span className="font-medium">{phone}</span></p>}
          {!success && error && <p className="text-sm text-muted-foreground break-all">Erro: {error}</p>}
          <Button onClick={() => navigate("/integrations")} className="w-full">Voltar para Integrações</Button>
        </CardContent>
      </Card>
    </div>
  );
}
